import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Firebase Firestore config
import { collection, addDoc, getDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

const CreateOfferPage = () => {
  const [offerData, setOfferData] = useState({
    name: '',
    description: '',
    image: '',
    country: '',
    city: '',
    type: '',
    price: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isPartner, setIsPartner] = useState(false); // Check if the user is a partner
  const navigate = useNavigate();

  const tripTypes = ['رحلة', 'رحلة حلال', 'حج وعمرة']; // Predefined trip types in Arabic

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOfferData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Check if the logged-in user is a partner
  useEffect(() => {
    const checkUserRole = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.role === 'partner') {
              setIsPartner(true); // User is a partner
            } else {
              setErrorMessage('فقط الشركاء يمكنهم إنشاء العروض.');
            }
          } else {
            setErrorMessage('لم يتم العثور على بيانات المستخدم في قاعدة البيانات.');
          }
        } catch (error) {
          setErrorMessage('خطأ في جلب بيانات المستخدم: ' + error.message);
        }
      } else {
        setErrorMessage('لم يقم المستخدم بتسجيل الدخول.');
      }
    };

    checkUserRole();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!isPartner) {
      setErrorMessage('يجب أن تكون شريكًا لإنشاء عرض.');
      return;
    }

    if (
      !offerData.name ||
      !offerData.description ||
      !offerData.image ||
      !offerData.country ||
      !offerData.city ||
      !offerData.type ||
      !offerData.price
    ) {
      setErrorMessage('الرجاء ملء جميع الحقول.');
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        setErrorMessage('لم يقم المستخدم بتسجيل الدخول.');
        return;
      }

      // Save to Firestore
      await addDoc(collection(db, 'offers'), {
        ...offerData,
        price: parseFloat(offerData.price),
        createdAt: new Date(),
        userId: user.uid, // Store the user ID
      });

      setSuccessMessage('تم إنشاء العرض بنجاح!');
      setOfferData({
        name: '',
        description: '',
        image: '',
        country: '',
        city: '',
        type: '',
        price: '',
      });

      // Redirect to the offers page
      setTimeout(() => navigate('/OffersPage'), 2000);
    } catch (error) {
      setErrorMessage('فشل في إنشاء العرض. يرجى المحاولة مرة أخرى.');
      console.error('Error creating offer:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-300 flex justify-center items-center">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-3xl">
        <h1 className="text-3xl font-bold text-center mb-6">إنشاء عرض جديد</h1>

        {errorMessage && <p className="text-red-500 text-center font-semibold mb-4">{errorMessage}</p>}
        {successMessage && <p className="text-green-500 text-center font-semibold mb-4">{successMessage}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">اسم العرض</label>
            <input
              type="text"
              name="name"
              value={offerData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="أدخل اسم العرض"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">الوصف</label>
            <textarea
              name="description"
              value={offerData.description}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="أدخل وصفًا موجزًا"
              rows="4"
              required
            ></textarea>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">رابط الصورة</label>
            <input
              type="url"
              name="image"
              value={offerData.image}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="أدخل رابط الصورة"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">الدولة</label>
            <input
              type="text"
              name="country"
              value={offerData.country}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="أدخل الدولة"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">المدينة</label>
            <input
              type="text"
              name="city"
              value={offerData.city}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="أدخل المدينة"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">نوع الرحلة</label>
            <select
              name="type"
              value={offerData.type}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              required
            >
              <option value="">اختر النوع</option>
              {tripTypes.map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">السعر (بالدولار الأمريكي)</label>
            <input
              type="number"
              name="price"
              value={offerData.price}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-lg"
              placeholder="أدخل السعر"
              step="0.01"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-[#1CC752] text-white py-2 px-4 rounded-lg hover:bg-[rgb(0,146,146)]"
          >
            إنشاء العرض
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateOfferPage;
